import React, { useEffect, useRef, useState } from "react";
import { HiFilter, HiSearch } from "react-icons/hi";
import axios from "axios";
import TableLoader from "Components/Common/Loader/TableLoader";
import { MdCancel } from "react-icons/md";
import Pagination from "Components/Pagination/Pagination";
import exportToExcel from "utils/exportToExcel";
import Swal from "sweetalert2";
import { FaFileExcel } from "react-icons/fa";

const CoinHistoriesTable = () => {
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("All Statuses");
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const statusDropdownRef = useRef(null);

  const [transactionHistories, setTransactionHistories] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  const toggleStatusDropdown = () =>
    setIsStatusDropdownOpen(!isStatusDropdownOpen);

  const handleStatusClick = (statusOption, statusValue) => {
    setSelectedStatus(statusOption);
    setIsStatusDropdownOpen(false);
    if (statusOption === "Custom Date") {
      setShowCustomDate(true);
      setStartDate("");
      setEndDate("");
      setFilters((prev) => {
        const updated = { ...prev };
        delete updated.status;
        return updated;
      });
    } else {
      setShowCustomDate(false);
      setStartDate("");
      setEndDate("");

      const newFilters = { ...filters };
      delete newFilters.startDate;
      delete newFilters.endDate;
      delete newFilters.dateFilter;

      if (statusValue) newFilters.status = statusValue;
      else delete newFilters.status;

      setFilters(newFilters);
      setCurrentPage(1);
      getTransactionHistories(1, newFilters, searchQuery);
    }
  };

  const handleDateChange = (type, value) => {
    let newFilters = { ...filters };

    if (type === "startDate") {
      setStartDate(value);
      if (value) {
        const start = new Date(value);
        start.setHours(0, 0, 0, 0);
        newFilters.startDate = start.toISOString();
      } else delete newFilters.startDate;
    } else if (type === "endDate") {
      setEndDate(value);
      if (value) {
        const end = new Date(value);
        end.setHours(23, 59, 59, 999);
        newFilters.endDate = end.toISOString();
      } else delete newFilters.endDate;
    }

    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClickOutside = (event) => {
    if (
      statusDropdownRef.current &&
      !statusDropdownRef.current.contains(event.target)
    ) {
      setIsStatusDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!hasInitialLoad) setHasInitialLoad(true);
  }, []);

  useEffect(() => {
    if (!hasInitialLoad) return;
    const delaySearch = setTimeout(() => {
      getTransactionHistories(currentPage, filters, searchQuery);
    }, 400);
    return () => clearTimeout(delaySearch);
  }, [searchQuery, filters, currentPage, hasInitialLoad]);

  const getTransactionHistories = async (
    page = 1,
    filters = {},
    searchQuery = ""
  ) => {
    try {
      const params = new URLSearchParams({ page, limit: 5 });
      if (filters?.startDate) params.append("startDate", filters.startDate);
      if (filters?.endDate) params.append("endDate", filters.endDate);
      if (filters?.startDate || filters?.endDate)
        params.append("dateFilter", "custom");
      if (filters?.status) params.append("status", filters.status);
      if (searchQuery) params.append("searchTerm", searchQuery);

      setLoading(true);

      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL
        }/transaction-history/get-all-transaction-histories?${params.toString()}`
      );

      setTransactionHistories(response.data.data || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching transaction histories:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleExport = async () => {
    try {
      Swal.fire({
        title: "Preparing Export...",
        text: "Fetching filtered data, please wait...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const params = new URLSearchParams({
        exportAll: true,
        limit: "all",
      });

      if (filters?.startDate) params.append("startDate", filters.startDate);
      if (filters?.endDate) params.append("endDate", filters.endDate);
      if (filters?.startDate || filters?.endDate)
        params.append("dateFilter", "custom");
      if (filters?.status) params.append("status", filters.status);
      if (searchQuery) params.append("searchTerm", searchQuery);

      const { data } = await axios.get(
        `${
          process.env.REACT_APP_API_URL
        }/transaction-history/get-all-transaction-histories?${params.toString()}`
      );

      if (!data || data.length === 0) {
        Swal.fire(
          "No Data",
          "No records found for the selected filters.",
          "info"
        );
        return;
      }

      const exportData = data.map((t) => ({
        Date: new Date(t.createdAt).toLocaleDateString(),
        Time: new Date(t.createdAt).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
        "User Name": t.userID?.fullName || "--",
        Phone: t.userID?.phone ? `+91 ${t.userID.phone.slice(2)}` : "--",
        Amount: `₹${t.amount}`,
        "Payment Gateway": t.paymentGateway?.toUpperCase() || "--",
        "Txn ID / UTR":
          t.payment_gateway_details?.gateway_payment_id ||
          t.payment_gateway_details?.utr ||
          t.payment_gateway_details?.transactionId ||
          "--",
        Status: t.status,
      }));

      exportToExcel(exportData, "Transaction_Histories");
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to export data.", "error");
    }
  };

  return (
    <div className="py-5">
      {/* Filters */}
      <div className="flex flex-col items-center justify-start gap-2 space-y-4 rounded-2xl bg-white p-5 shadow-sm md:flex-row md:space-y-0">
        {/* Status Dropdown */}
        <div
          className="md:min-w-1/5 relative w-full md:w-1/5"
          ref={statusDropdownRef}
        >
          <button
            className="flex w-full items-center rounded-md border bg-white px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
            onClick={toggleStatusDropdown}
          >
            <HiFilter color="#c51369" className="mr-2 h-5 w-5" />
            {selectedStatus}
          </button>

          {isStatusDropdownOpen && (
            <div className="absolute left-0 z-10 mt-2 w-full rounded-md border bg-white shadow-lg">
              <ul className="py-2">
                <li
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                  onClick={() => handleStatusClick("All Statuses", null)}
                >
                  All Statuses
                </li>
                <li
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                  onClick={() => handleStatusClick("SUCCESS", "SUCCESS")}
                >
                  SUCCESS
                </li>
                <li
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                  onClick={() => handleStatusClick("FAILED", "FAILED")}
                >
                  FAILED
                </li>
                <li
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                  onClick={() => handleStatusClick("PENDING", "PENDING")}
                >
                  PENDING
                </li>
                <li
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                  onClick={() => handleStatusClick("Custom Date", null)}
                >
                  Custom Date
                </li>
              </ul>
            </div>
          )}
        </div>
        {showCustomDate && (
          <div className="mt-4 flex flex-col items-start gap-4 rounded-xl bg-white p-4 shadow-sm md:flex-row md:items-center md:gap-6">
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleDateChange("startDate", e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369] md:w-1/2"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => handleDateChange("endDate", e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369] md:w-1/2"
            />
          </div>
        )}

        {/* Search Input */}
        <div className="md:min-w-2/5 relative w-full md:w-2/5">
          <input
            type="text"
            placeholder="Search by phone/txn Id/UTR"
            value={searchQuery}
            onChange={(e) => {
              setCurrentPage(1);
              setSearchQuery(e.target.value);
            }}
            className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
          />
          <HiSearch className="absolute left-3 top-3 text-gray-500" />
          {searchQuery && (
            <button
              type="button"
              onClick={clearSearch}
              className="text-md absolute right-2 top-2 rounded px-2 py-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
            >
              <MdCancel />
            </button>
          )}
        </div>

        <button
          onClick={handleExport}
          className="flex items-center gap-2 rounded-md bg-[#c51369] px-4 py-2 text-white hover:bg-[#a01052]"
        >
          <FaFileExcel /> Export
        </button>
      </div>

      {loading ? (
        <TableLoader />
      ) : (
        <div className="mx-auto mt-5 w-auto rounded-t-2xl">
          <div className="overflow-x-auto rounded-t-2xl">
            <table className="w-full rounded-t-2xl text-left text-sm">
              <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
                <tr className="text-center">
                  <th className="px-6 py-3">Sl No.</th>
                  <th className="px-6 py-3">Date</th>
                  <th className="px-6 py-3">Time</th>
                  <th className="px-6 py-3">User</th>
                  <th className="px-6 py-3">Amount</th>
                  <th className="px-6 py-3">
                    Payments & <br /> Txn_id / UTR
                  </th>
                  <th className="px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="text-center">
                {transactionHistories?.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-md py-6 text-gray-500">
                      {selectedStatus === "PENDING"
                        ? "No Pending Found"
                        : selectedStatus === "FAILED"
                        ? "No Failed Found"
                        : selectedStatus === "SUCCESS"
                        ? "No Approved Found"
                        : "No Records Found"}
                    </td>
                  </tr>
                ) : (
                  transactionHistories.map((transaction, index) => (
                    <tr
                      key={transaction._id}
                      className="text-md bg-white tracking-wide text-gray-700"
                    >
                      <td className="border px-6 py-4">
                        {(currentPage - 1) * 5 + index + 1}
                      </td>
                      <td className="border px-6 py-4">
                        {new Date(transaction.createdAt).toLocaleDateString()}
                      </td>
                      <td className="border px-6 py-4">
                        {new Date(transaction.createdAt).toLocaleTimeString(
                          [],
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          }
                        )}
                      </td>
                      <td className="border px-3 py-4">
                        <div className="flex flex-col items-center">
                          <img
                            src={transaction.userID?.selected_Avatar?.avatarUrl}
                            alt="avatar"
                            className="h-10 w-10 rounded-full"
                          />
                          <span className="ml-4">
                            {transaction.userID?.fullName}
                          </span>
                          <span className="ml-4">
                            +91 {transaction.userID?.phone.slice(2)}
                          </span>
                        </div>
                      </td>
                      <td className="border px-6 py-4">
                        ₹{transaction.amount}
                      </td>
                      <td className="border px-6 py-4 text-center">
                        <div className="flex flex-col items-center">
                          <span>
                            {transaction.paymentGateway?.toUpperCase() || "--"}
                          </span>
                          <span>
                            {transaction.payment_gateway_details
                              ?.gateway_payment_id ||
                              transaction.payment_gateway_details?.utr ||
                              transaction.payment_gateway_details
                                ?.transactionId ||
                              "--"}
                          </span>
                        </div>
                      </td>
                      <td className="border px-6 py-4">
                        <span
                          className={`rounded-full px-2 py-1 text-xs ${
                            transaction.status === "SUCCESS"
                              ? "bg-green-100 text-green-800"
                              : transaction.status === "FAILED"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {transaction.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      )}
    </div>
  );
};

export default CoinHistoriesTable;
